import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { MessageList } from '@/components/ChatInterface/MessageList';
import { MessageInput } from '@/components/ChatInterface/MessageInput';
import { GenerateFileMenu } from '@/components/ChatInterface/GenerateFileMenu';
import { StreamingMessage } from '@/components/ChatInterface/StreamingMessage';
import { EmptyState } from '@/components/ChatInterface/EmptyState';
import { ErrorState } from '@/components/ChatInterface/ErrorState';
import { Message } from '@/services/conversations';
import { ConversationsSidebar } from './ConversationsSidebar';
import { conversationsService, Conversation } from '@/services/conversations';
import { chatbotsService, Chatbot } from '@/services/chatbots';
import { useRAGStream } from '@/hooks/useRAGStream';
import { useKeyboardShortcuts } from '@/hooks/useKeyboardShortcuts';
import notify from '@/lib/notify';
import { Button } from '@/components/Common/button';
import { ArrowLeftIcon, Info, Settings2Icon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useGenieArtifactUsage } from '@/hooks/useGenieArtifactUsage';
import { UsageIndicator } from '@/components/Artifact/UsageIndicator';
import { usePermissions } from '@/hooks/usePermissions';

const DEMO_CTA_URL = import.meta.env.VITE_DEMO_CTA_URL || 'https://geniefactory.fr/';

export const ChatPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [messages, setMessages] = useState<Message[]>([]);
  const [currentConversation, setCurrentConversation] = useState<Conversation | null>(null);
  const [currentChatbot, setCurrentChatbot] = useState<Chatbot | null>(null);
  const [chatbots, setChatbots] = useState<Chatbot[]>([]);
  const [sidebarRefreshToken, setSidebarRefreshToken] = useState(0);
  const [starterQuestions, setStarterQuestions] = useState<string[]>([]);
  const [isLoadingStarterQuestions, setIsLoadingStarterQuestions] = useState(false);
  const [isChatbotsLoaded, setIsChatbotsLoaded] = useState(false);
  const messagesRef = useRef<Message[]>([]);
  const conversationRef = useRef<Conversation | null>(null);
  const [searchParams] = useSearchParams();
  const streamState = useRAGStream();
  const { canRead } = usePermissions();
  const canManageChatbots = canRead('chatbots');
  const { summary: usageSummary, refetch: refetchUsage } = useGenieArtifactUsage();
  const [activeThemeFilter, setActiveThemeFilter] = useState<string>('all');

  const context = useMemo(
    () => ({
      domain_id: searchParams.get('domain_id') || undefined,
      use_case_id: searchParams.get('use_case_id') || undefined,
      cadrage_id: searchParams.get('cadrage_id') || undefined,
    }),
    [searchParams]
  );

  const chatbotIdFromUrl = searchParams.get('chatbot_id');
  const conversationIdFromUrl = searchParams.get('conversation_id');

  const availableThemeFilters = useMemo(() => {
    const source = currentChatbot?.theme_filters || [];
    const normalized = source
      .filter((theme): theme is string => typeof theme === 'string')
      .map((theme) => theme.trim().toLowerCase())
      .filter(Boolean);
    return Array.from(new Set(normalized));
  }, [currentChatbot?.theme_filters]);

  const formatThemeLabel = useCallback((themeCode: string) => {
    const withSpaces = themeCode.split('_').join(' ').trim();
    if (!withSpaces) return themeCode;
    return withSpaces.charAt(0).toUpperCase() + withSpaces.slice(1);
  }, []);

  const buildContextWithTheme = useCallback(() => {
    const scopedContext = {
      ...context,
      ...(activeThemeFilter !== 'all' ? { theme_codes: [activeThemeFilter] } : {}),
    };
    const hasFilters = Object.values(scopedContext).some((value) => {
      if (Array.isArray(value)) return value.length > 0;
      return Boolean(value);
    });
    return hasFilters ? scopedContext : undefined;
  }, [context, activeThemeFilter]);

  useEffect(() => {
    if (activeThemeFilter === 'all') return;
    if (!availableThemeFilters.includes(activeThemeFilter)) {
      setActiveThemeFilter('all');
    }
  }, [availableThemeFilters, activeThemeFilter]);

  useEffect(() => {
    messagesRef.current = messages;
  }, [messages]);

  useEffect(() => {
    conversationRef.current = currentConversation;
  }, [currentConversation]);

  useKeyboardShortcuts({
    onEscape: () => {
      if (streamState.isStreaming) {
        streamState.cancel();
      }
    },
  });

  const loadChatbots = useCallback(async () => {
    try {
      const data = await chatbotsService.list();
      setChatbots(data);
      if (!chatbotIdFromUrl && !conversationIdFromUrl) {
        const defaultChatbot = data.find((c) => c.is_default) || data[0];
        setCurrentChatbot(defaultChatbot);
      }
    } catch (error) {
      console.error('Error loading chatbots:', error);
    } finally {
      setIsChatbotsLoaded(true);
    }
  }, [chatbotIdFromUrl, conversationIdFromUrl]);

  const handleSelectConversation = useCallback(
    async (conversationId: string) => {
      try {
        const conversation = await conversationsService.get(conversationId);
        setCurrentConversation(conversation);

        if (conversation.chatbot_id && chatbots.length > 0) {
          const chatbot = chatbots.find((c) => c.id === conversation.chatbot_id);
          if (chatbot) {
            setCurrentChatbot(chatbot);
          }
        }

        setMessages(conversation.messages || []);
      } catch (error) {
        console.error('Error loading conversation:', error);
        notify.error(t('chatPage.loadConversationError'));
      }
    },
    [chatbots, t]
  );

  useEffect(() => {
    loadChatbots();
  }, [loadChatbots]);

  useEffect(() => {
    if (chatbots.length === 0) return;

    if (conversationIdFromUrl) {
      handleSelectConversation(conversationIdFromUrl);
    } else if (chatbotIdFromUrl) {
      const chatbot = chatbots.find((c) => c.id === chatbotIdFromUrl);
      if (chatbot) {
        setCurrentChatbot(chatbot);
      }
    }
  }, [chatbots, conversationIdFromUrl, chatbotIdFromUrl, handleSelectConversation]);

  const handleNewConversation = () => {
    setCurrentConversation(null);
    setMessages([]);
  };

  const loadStarterQuestions = useCallback(async () => {
    if (currentConversation || messages.length > 0) return;
    if (!isChatbotsLoaded) return;
    if (chatbots.length > 0 && !currentChatbot?.id) return;
    try {
      setIsLoadingStarterQuestions(true);
      const questions = await conversationsService.getStarterQuestions({
        chatbotId: currentChatbot?.id,
        limit: 4,
      });
      setStarterQuestions(questions);
    } catch (error) {
      console.error('Error loading starter questions:', error);
      setStarterQuestions([]);
    } finally {
      setIsLoadingStarterQuestions(false);
    }
  }, [currentConversation, messages.length, currentChatbot?.id, isChatbotsLoaded, chatbots.length]);

  useEffect(() => {
    if (currentConversation || messages.length > 0) {
      setStarterQuestions([]);
      return;
    }
    if (!isChatbotsLoaded) return;
    if (chatbots.length > 0 && !currentChatbot?.id) return;
    loadStarterQuestions();
  }, [
    currentChatbot?.id,
    currentConversation,
    messages.length,
    loadStarterQuestions,
    isChatbotsLoaded,
    chatbots.length,
  ]);

  /**
   * Returns the active conversation id, creating the conversation on demand.
   * Shared by message sending and file attachment (attach before first message).
   */
  const ensureConversation = useCallback(async (): Promise<string> => {
    const existing = conversationRef.current;
    if (existing?.id) return existing.id;

    const newConversation = await conversationsService.create({
      chatbot_id: currentChatbot?.id,
    });
    conversationRef.current = newConversation;
    setCurrentConversation(newConversation);
    setSidebarRefreshToken((prev) => prev + 1);
    return newConversation.id;
  }, [currentChatbot?.id]);

  const handleSendMessage = async (query: string) => {
    try {
      const contextWithTheme = buildContextWithTheme();
      const conversationId = await ensureConversation();

      const userMessage: Message = {
        id: `temp-user-${Date.now()}`,
        conversation_id: conversationId,
        content: query,
        is_user: true,
        metadata: { sources: [] },
        timestamp: new Date().toISOString(),
        evaluation: null,
      };
      setMessages((prev) => [...prev, userMessage]);

      await streamState.sendMessage(query, {
        context: contextWithTheme,
        conversationId,
        chatbotId: currentChatbot?.id,
      });
    } catch (error) {
      console.error('Error sending message:', error);
      notify.error(t('chatPage.sendError'));
    }
  };

  useEffect(() => {
    if (!streamState.isStreaming && streamState.lastResponse && !streamState.error) {
      const response = streamState.lastResponse;
      const activeConversation = conversationRef.current;
      if (!activeConversation?.id) return;

      if (response.conversation.title !== activeConversation.title) {
        setCurrentConversation(response.conversation);
      }
      setSidebarRefreshToken((prev) => prev + 1);

      setMessages((prev) => {
        const tempUserMessage = [...prev]
          .reverse()
          .find((m: Message) => m.is_user && m.id.startsWith('temp-user-'));
        const next = tempUserMessage
          ? prev.filter((m) => m.id !== tempUserMessage.id)
          : [...prev];
        return [...next, response.user_message, response.assistant_message];
      });

      streamState.reset();
      refetchUsage();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [streamState.isStreaming, streamState.lastResponse, streamState.error]);

  const headerTitle = currentChatbot
    ? t('chatPage.titleWithAssistant', { name: currentChatbot.name })
    : currentConversation?.title || t('chatPage.titleDefault');

  const goToConversationsList = () =>
    navigate(currentChatbot?.id ? `/conversations?chatbot_id=${currentChatbot.id}` : '/conversations');

  const goToChatbotSettings = () =>
    navigate(currentChatbot?.id ? `/chatbots?edit_chatbot_id=${currentChatbot.id}` : '/chatbots');

  return (
    <div className="flex h-full min-h-0 w-full flex-col bg-background">
      {/* Slim top bar — ChatGPT-style: back, title, settings */}
      <header className="flex shrink-0 items-center justify-between gap-3 border-b border-border/60 bg-muted/10 px-2 py-2 sm:px-3">
        <div className="flex min-w-0 flex-1 items-center gap-1">
          <Button
            variant="ghost"
            size="icon"
            className="shrink-0 text-muted-foreground hover:text-foreground"
            onClick={goToConversationsList}
            aria-label={t('chatPage.backAria')}
            title={t('chatPage.backToConversations')}
          >
            <ArrowLeftIcon className="h-5 w-5" />
          </Button>
          <h1 className="truncate text-sm font-medium text-foreground sm:text-base">{headerTitle}</h1>
        </div>
        <UsageIndicator summary={usageSummary} />
        {currentConversation?.id && (
          <GenerateFileMenu
            conversationId={currentConversation.id}
            disabled={streamState.isStreaming}
          />
        )}
        {canManageChatbots && (
          <Button
            variant="ghost"
            size="icon"
            className="shrink-0 text-muted-foreground hover:text-foreground"
            onClick={goToChatbotSettings}
            disabled={!currentChatbot}
            aria-label={t('chatPage.settingsAria')}
          >
            <Settings2Icon className="h-5 w-5" />
          </Button>
        )}
      </header>

      <div className="flex min-h-0 flex-1">
        <ConversationsSidebar
          currentConversationId={currentConversation?.id}
          onSelectConversation={handleSelectConversation}
          onNewConversation={handleNewConversation}
          chatbotId={currentChatbot?.id}
          refreshToken={sidebarRefreshToken}
        />

        <main className="flex min-h-0 flex-1 flex-col bg-background">
          <div className="min-h-0 flex-1 overflow-y-auto">
            <div className="w-full px-4 py-6 sm:px-6 md:px-10 lg:px-14 xl:px-16">
              {messages.length === 0 && !streamState.isStreaming && (
                <EmptyState
                  onSuggestedQuestion={handleSendMessage}
                  suggestedQuestions={starterQuestions}
                  isLoadingSuggestions={isLoadingStarterQuestions}
                  chatbotName={currentChatbot?.name}
                />
              )}

              {messages.length > 0 && (
                <>
                  <MessageList messages={messages} conversationId={currentConversation?.id} />

                  {streamState.isStreaming && (
                    <div className="mt-2">
                      <StreamingMessage
                        thinking={streamState.thinking}
                        sources={streamState.sources}
                        currentMessage={streamState.currentMessage}
                      />
                    </div>
                  )}

                  {streamState.error && !streamState.isQuotaExceeded && (
                    <div className="mt-6">
                      <ErrorState
                        error={streamState.error}
                        onRetry={() => {
                          const lastUserMessage = [...messages].reverse().find((m: Message) => m.is_user);
                          if (lastUserMessage) {
                            handleSendMessage(lastUserMessage.content);
                          }
                        }}
                      />
                    </div>
                  )}
                </>
              )}
            </div>
          </div>

          <div className="shrink-0 border-t border-border/50 bg-background/95 px-4 py-3 backdrop-blur-sm sm:px-6 md:px-10 lg:px-14 xl:px-16">
              {availableThemeFilters.length > 0 && (
                <div className="mb-3">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="mr-1 inline-flex items-center gap-1 text-xs text-muted-foreground">
                      {t('chatPage.theme')}
                      <button
                        type="button"
                        className="inline-flex rounded-full text-muted-foreground hover:text-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                        title={t('chatPage.themeFilterHint')}
                        aria-label={t('chatPage.themeFilterHint')}
                      >
                        <Info className="h-3.5 w-3.5" aria-hidden />
                      </button>
                    </span>
                    <button
                      type="button"
                      onClick={() => setActiveThemeFilter('all')}
                      className={`rounded-full border px-2.5 py-1 text-xs transition-colors ${
                        activeThemeFilter === 'all'
                          ? 'border-primary bg-primary text-primary-foreground'
                          : 'border-border bg-background text-foreground hover:bg-muted/80'
                      }`}
                    >
                      {t('chatPage.allThemes')}
                    </button>
                    {availableThemeFilters.map((themeCode) => (
                      <button
                        key={themeCode}
                        type="button"
                        onClick={() => setActiveThemeFilter(themeCode)}
                        className={`rounded-full border px-2.5 py-1 text-xs transition-colors ${
                          activeThemeFilter === themeCode
                            ? 'border-primary bg-primary text-primary-foreground'
                            : 'border-border bg-background text-foreground hover:bg-muted/80'
                        }`}
                      >
                        {formatThemeLabel(themeCode)}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Quota exceeded banner — replaces input in demo mode */}
              {streamState.isQuotaExceeded ? (
                <div className="rounded-lg border border-amber-200 bg-amber-50 dark:border-amber-800 dark:bg-amber-950 p-4">
                  <p className="text-sm text-amber-800 dark:text-amber-200 mb-3">
                    {t('demo.quotaExceeded')}
                  </p>
                  <a
                    href={DEMO_CTA_URL}
                    className="inline-flex items-center rounded-md bg-amber-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-amber-700 transition-colors"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {t('demo.ctaButton')}
                  </a>
                </div>
              ) : (
                <MessageInput
                  onSend={handleSendMessage}
                  disabled={streamState.isStreaming}
                  autoFocus={true}
                  variant="chat"
                  conversationId={currentConversation?.id}
                  onEnsureConversation={ensureConversation}
                  shortcutButtons={currentChatbot?.shortcut_buttons || undefined}
                />
              )}
          </div>
        </main>
      </div>
    </div>
  );
};
